import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, InputNumber, DatePicker, Select, Row, Col, message, Card, Divider, Tag } from 'antd';
import { useLoanRepayment } from '../../store/loanRepaymentContext';
import { useCustomer } from '../../store/customerContext';
import { useCurrency } from '../../store/currencyContext';
import dayjs from 'dayjs';
import { LoanRepayment } from '../../types/loanRepayment';
import { useTranslation } from "react-i18next";
import { useReactToPrint } from 'react-to-print';
import PaymentInvoice from '../Print/PaymentInvoice';
import { UserOutlined, PhoneOutlined, HomeOutlined } from '@ant-design/icons';

const PAYMENT_METHODS = [
    { value: "cash", label: "Cash" },
    { value: "bank", label: "Bank" },
    { value: "other", label: "Other" },
];

interface Props {
    editLoanRepayment?: LoanRepayment;
    onFinishEdit?: () => void;
}

const LoanRepaymentForm: React.FC<Props> = ({ editLoanRepayment, onFinishEdit }) => {
    const [form] = Form.useForm();
    const { addLoanRepayment, updateLoanRepayment } = useLoanRepayment();
    const { customers, fetchCustomers } = useCustomer();
    const { currencies } = useCurrency();
    const { t } = useTranslation();

    // === PRINT LOGIC ===
    const [newRepayment, setNewRepayment] = useState<LoanRepayment | null>(null);
    const printRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: t('loan_repayment_invoice') as string,
    });

    // Prepare currency list for Select
    const currencyList = currencies.map(c => ({
        value: c.code,
        label: c.symbol ? `${c.code} (${c.symbol})` : c.code
    }));

    // Customer Select Options with Custom Labels
    const customerOptions = (customers || []).map(c => {
        const balanceString = (c.balances || [])
            .map(b => `${b.currency}: ${b.balance}`)
            .join(', ');
        return {
            value: c.id,
            label: (
                <div>
                    <b>{c.name}</b>
                    {c.phone && <span style={{ color: '#888' }}> ({c.phone})</span>}
                    <div style={{ fontSize: 12, color: '#888' }}>{c.address}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{balanceString}</div>
                </div>
            ),
        };
    });

    // Track selected customer object
    const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

    useEffect(() => {
        fetchCustomers?.();
        const DEFAULT_CURRENCY = currencies.find(c => c.isDefault)?.code || "IQD";
        if (editLoanRepayment) {
            form.setFieldsValue({
                ...editLoanRepayment,
                date: editLoanRepayment.date ? dayjs(editLoanRepayment.date) : dayjs(),
            });
            if (editLoanRepayment.customer) {
                const found = customers.find(c => c.id === editLoanRepayment.customer);
                setSelectedCustomer(found || null);
            }
        } else {
            form.setFieldsValue({ date: dayjs(), amount: 0, currency: DEFAULT_CURRENCY });
            setSelectedCustomer(null);
        }
        // eslint-disable-next-line
    }, []);

    // Helper to get customer name
    const getCustomerName = (customerId?: string, customerName?: string): string => {
        if (customerId) {
            const found = customers.find(c => c.id === customerId);
            return found?.name || "-";
        }
        return customerName || "-";
    };

    // Payment method label helper
    const getPaymentMethodLabel = (code?: string) => {
        return PAYMENT_METHODS.find(m => m.value === code)?.label || "-";
    };

    // When select changes, update selectedCustomer
    const handleCustomerChange = (id: string) => {
        const found = customers.find(c => c.id === id);
        setSelectedCustomer(found || null);
        // If switching to select, clear direct input
        if (found) {
            form.setFieldsValue({ customerName: "", customerPhone: "" });
        }
    };

    const onFinish = (values: any) => {
        if (!values.amount || values.amount <= 0) {
            message.error(t('enter_valid_amount'));
            return;
        }
        if (!values.customer && !values.customerName) {
            message.error(t('select_or_enter_customer'));
            return;
        }
        const formatted = {
            ...values,
            date: values.date ? values.date.toISOString() : undefined,
        };
        if (editLoanRepayment) {
            updateLoanRepayment({ ...editLoanRepayment, ...formatted });
            onFinishEdit?.();
        } else {
            addLoanRepayment(formatted).then((created: LoanRepayment) => {
                setNewRepayment(created);
                setTimeout(() => handlePrint(), 400); // Print after save
                form.resetFields();
                setSelectedCustomer(null);
                const DEFAULT_CURRENCY = currencies.find(c => c.isDefault)?.code || "IQD";
                form.setFieldsValue({ currency: DEFAULT_CURRENCY });
            });
        }
    };

    return (
        <div style={{
            maxWidth: 730,
            margin: '32px auto',
            background: 'linear-gradient(120deg, #fafbfc 80%, #f5f7fa 100%)',
            borderRadius: 22,
            boxShadow: '0 4px 24px rgba(20,40,90,0.06)',
            padding: '30px 25px 25px 25px'
        }}>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                autoComplete="off"
            >
                <Row gutter={28} style={{ marginBottom: 0 }}>
                    <Col xs={24} md={12}>
                        <Form.Item label={t('customer')}>
                            <Form.Item name="customer" noStyle>
                                <Select
                                    options={customerOptions}
                                    placeholder={t('select_customer')}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    onChange={handleCustomerChange}
                                    filterOption={(input, option) =>
                                        (option?.label?.props?.children?.[0]?.props?.children?.toString().toLowerCase() || '')
                                            .includes(input.toLowerCase())
                                    }
                                    style={{ fontWeight: 600 }}
                                />
                            </Form.Item>
                            {selectedCustomer && (
                                <Card
                                    size="small"
                                    bordered={false}
                                    style={{
                                        marginTop: 10,
                                        background: 'linear-gradient(90deg,#f3f8fd 80%, #e5edfb 100%)',
                                        borderRadius: 16,
                                        boxShadow: '0 2px 8px #eef3fa',
                                        padding: '13px 20px'
                                    }}
                                    bodyStyle={{ padding: 0 }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                        <div>
                                            <UserOutlined style={{ color: "#94a3b8", fontSize: 22, marginRight: 4 }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 14, color: "#64748b" }}>
                                                <PhoneOutlined /> {selectedCustomer.phone} &nbsp;&nbsp;
                                                <HomeOutlined /> {selectedCustomer.address}
                                            </div>
                                            <div style={{ marginTop: 6, fontWeight: 600, color: "#6b7280" }}>
                                                {t('balances')}:
                                                {selectedCustomer.balances && selectedCustomer.balances.length > 0 ? (
                                                    <span style={{ marginLeft: 8 }}>
                                                        {selectedCustomer.balances.map((b: any, i: number) => (
                                                            <Tag
                                                                key={i}
                                                                color={b.balance < 0 ? "volcano" : b.balance > 0 ? "blue" : "default"}
                                                                style={{ fontSize: 13, marginRight: 6, padding: "2px 10px", borderRadius: 9, border: 0 }}
                                                            >
                                                                {t('currency')}: <b>{b.currency}</b> &nbsp;
                                                                {t('balance')}: <b>{Number(b.balance).toLocaleString()}</b>
                                                            </Tag>
                                                        ))}
                                                    </span>
                                                ) : <Tag color="default">{t('no_balance')}</Tag>}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            )}
                        </Form.Item>
                        <Form.Item name="customerName" label={t('customerName')} style={{ marginBottom: 8 }}>
                            <Input
                                placeholder={t('customer_name_if_direct') as string}
                                prefix={<UserOutlined />}
                                style={{ borderRadius: 7, fontSize: 15 }}
                            />
                        </Form.Item>
                        <Form.Item name="customerPhone" label={t('customerPhone')} style={{ marginBottom: 8 }}>
                            <Input
                                placeholder={t('customer_phone_if_direct') as string}
                                prefix={<PhoneOutlined />}
                                style={{ borderRadius: 7, fontSize: 15 }}
                            />
                        </Form.Item>
                        <Form.Item name="repaymentNumber" label={t('repaymentNumber')} rules={[{ required: true, message: t('required') as string }]}>
                            <Input
                                placeholder={t('repayment_number') as string}
                                style={{ borderRadius: 7, fontSize: 15 }}
                            />
                        </Form.Item>
                        <Form.Item name="date" label={t('date')} rules={[{ required: true, message: t('required') as string }]}>
                            <DatePicker
                                style={{ width: "100%", borderRadius: 7, fontSize: 15 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="amount" label={t('amount')} rules={[{ required: true, message: t('required') as string }]}>
                            <InputNumber min={0}
                                style={{ width: "100%", borderRadius: 7, fontSize: 15 }}
                            />
                        </Form.Item>
                        <Form.Item name="currency" label={t('currency')} rules={[{ required: true, message: t('required') as string }]}>
                            <Select
                                options={currencyList}
                                placeholder={t('currency')}
                                showSearch
                                optionFilterProp="label"
                                style={{ fontWeight: 600 }}
                            />
                        </Form.Item>
                        <Form.Item name="paymentMethod" label={t('paymentMethod') as string}>
                            <Select
                                options={PAYMENT_METHODS}
                                placeholder={t('payment_method') as string}
                                allowClear
                                style={{ fontWeight: 600 }}
                            />
                        </Form.Item>
                        <Form.Item name="reference" label={t('reference')}>
                            <Input
                                placeholder={t('reference_number') as string}
                                style={{ borderRadius: 7, fontSize: 15 }}
                            />
                        </Form.Item>
                        <Form.Item name="note" label={t('note')}>
                            <Input.TextArea
                                placeholder={t('note') as string}
                                autoSize
                                style={{ borderRadius: 7, fontSize: 15 }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider style={{ margin: '24px 0 8px 0' }} />
                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        style={{
                            borderRadius: 10,
                            fontWeight: 600,
                            fontSize: 17,
                            height: 48,
                            letterSpacing: 1
                        }}
                        block
                    >
                        {editLoanRepayment ? t('update_repayment') : t('create_repayment')}
                    </Button>
                </Form.Item>
            </Form>
            {/* Hidden Payment Invoice for Print */}
            <div style={{ display: 'none' }}>
                {newRepayment && (
                    <PaymentInvoice
                        ref={printRef}
                        type="loan"
                        invoice={newRepayment}
                        getPartyName={() => getCustomerName(newRepayment.customer, newRepayment.customerName)}
                        getPaymentMethodLabel={getPaymentMethodLabel}
                    />
                )}
            </div>
        </div>
    );
};

export default LoanRepaymentForm;
